import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Badge, Card, Row, Col, Form, Alert } from 'react-bootstrap';

const EventList = ({ events, onEditEvent, onDeleteEvent, onSyncWithRemote, hasUnsavedChanges, isAuthenticated }) => {
    const [userRole, setUserRole] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterTeam, setFilterTeam] = useState('all');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    // Filter and sort events
    const filteredEvents = Object.entries(events || {})
        .filter(([id, event]) => {
            const q = searchTerm.toLowerCase();
            const matchesSearch =
                (event.name || '').toLowerCase().includes(q) ||
                (event.slug || '').toLowerCase().includes(q);
            const matchesTeamFilter = filterTeam === 'all' ||
                                    (filterTeam === 'team' && event.team) ||
                                    (filterTeam === 'individual' && !event.team);
            return matchesSearch && matchesTeamFilter;
        })
        .sort(([, a], [, b]) => {
            let aValue = a[sortBy] ?? '';
            let bValue = b[sortBy] ?? '';

            if (sortBy === 'submissiondate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            } else if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = String(bValue).toLowerCase();
            }

            if (sortOrder === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    // Directly fetch user role from backend without using callBackend
    const getUserRole = async () => {
        const token = localStorage.getItem('nssc-admin-token');
        if (!token) {
            setUserRole(0);
            return;
        }
        try {
            const resp = await axios.get(
                process.env.REACT_APP_BACKEND_URL + '/admin/user/role',
                {
                    headers: {
                        Authorization: token,
                    },
                }
            );
            if (resp && resp.data && resp.data.status === 200) {
                setUserRole(Number(resp.data.role));
            } else {
                setUserRole(0);
            }
        } catch (err) {
            // treat errors as unauthorized / visitor
            setUserRole(0);
        }
    };

    useEffect(() => {
        getUserRole();
    }, []);

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return '↕️';
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    return (
        <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
                <h4>Events Management</h4>
                <div className="d-flex gap-2">
                    {hasUnsavedChanges && (
                        <Badge bg="warning" text="dark">
                            Unsaved Changes
                        </Badge>
                    )}
                    {isAuthenticated && (
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={onSyncWithRemote}
                        >
                             Sync with GitHub
                        </Button>
                    )}
                </div>
            </Card.Header>
            <Card.Body>
                {/* Filters and Search */}
                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Control
                            type="text"
                            placeholder="Search events..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={filterTeam}
                            onChange={(e) => setFilterTeam(e.target.value)}
                        >
                            <option value="all">All Events</option>
                            <option value="team">Team Events</option>
                            <option value="individual">Individual Events</option>
                        </Form.Select>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Sort by Name</option>
                            <option value="slug">Sort by Slug</option>
                            <option value="submissiondate">Sort by Deadline</option>
                            <option value="backendId">Sort by Backend ID</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Button
                            variant="outline-secondary"
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        >
                            {sortOrder === 'asc' ? '↑' : '↓'}
                        </Button>
                    </Col>
                </Row>

                {filteredEvents.length === 0 ? (
                    <Alert variant="info">
                        No events found. {Object.keys(events || {}).length === 0 ? 'Add your first event!' : 'Try adjusting your search criteria.'}
                    </Alert>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>                              
                                    <th 
                                        style={{ cursor: 'pointer' }} 
                                        onClick={() => handleSort('name')}
                                    >
                                        Name {getSortIcon('name')}
                                    </th>
                                    <th>Type</th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSort('submissiondate')}
                                    >
                                        Deadline {getSortIcon('submissiondate')}
                                    </th>
                                    <th>Details</th>
                                    { (userRole === 2 || userRole === 4) && (
                                        <th>Actions</th>
                                    ) }
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEvents.map(([id, event]) => (
                                    <tr key={id}>
                                        <td>
                                            <strong>{event.name}</strong>
                                            {event.slug && (
                                                <div className="text-muted small">
                                                    Slug: {event.slug}
                                                    {event.backendId != null && ` · id ${event.backendId}`}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column gap-1">
                                                <Badge bg={event.team ? 'primary' : 'secondary'}>
                                                    {event.team ? `Team (${event.min}-${event.max})` : 'Individual'}
                                                </Badge>
                                                {event.psLink && <Badge bg="warning" text="dark">Problem Statement</Badge>}
                                            </div>
                                        </td>
                                        <td>
                                            {event.submissiondate ? formatDate(event.submissiondate) : 'Not set'}
                                        </td>
                                        <td>
                                            <div className="small">
                                                <div><strong>Prize:</strong> {event.prize || 'Not specified'}</div>
                                                <div><strong>Rules:</strong> {event.rules?.length || 0} rule(s)</div>
                                                {event.elink && (
                                                    <div>
                                                        <a 
                                                            href={event.elink} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                        >
                                                             External Link
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        { (userRole === 2 || userRole === 4) && (
                                            <td>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => onEditEvent(id, event)}
                                                    >
                                                         Edit
                                                    </Button>
                                                </div>
                                            </td>
                                        ) }
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                <div className="mt-3 text-muted small">
                    Showing {filteredEvents.length} of {Object.keys(events || {}).length} events
                </div>
            </Card.Body>
        </Card>
    );
};

export default EventList;